import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Badge, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import apiService from '../../services/api';

const StorageLocationTypeManagement = ({ organization, onBack }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchName, setSearchName] = useState('');
    const [searchCode, setSearchCode] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        ordinal: 0,
        is_active: true
    });

    useEffect(() => {
        if (organization) {
            loadItems();
        }
    }, [organization]);

    const loadItems = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getStorageLocationTypesByOrganization(organization.id);
            if (response.success) {
                setItems(response.data || []);
            } else {
                setError(response.error || 'Failed to load storage location types');
                setItems([]);
            }
        } catch (err) {
            console.error('Error loading storage location types:', err);
            setError('Failed to load storage location types');
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getFilteredAndSorted = () => {
        let filtered = items.filter(item => {
            const nameMatch = !searchName || (item.name && item.name.toLowerCase().includes(searchName.toLowerCase()));
            const codeMatch = !searchCode || (item.code && item.code.toLowerCase().includes(searchCode.toLowerCase()));
            return nameMatch && codeMatch;
        });

        if (!sortConfig.key) return filtered;

        return [...filtered].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? '';
            let bVal = b[sortConfig.key] ?? '';
            if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortConfig.direction === 'asc' ?
            <i className="bi bi-arrow-up ms-1"></i> :
            <i className="bi bi-arrow-down ms-1"></i>;
    };

    const handleShowModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ name: item.name || '', code: item.code || '', ordinal: item.ordinal ?? 0, is_active: item.is_active ?? true });
        } else {
            setEditingItem(null);
            setFormData({ name: '', code: '', ordinal: 0, is_active: true });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', code: '', ordinal: 0, is_active: true });
        setError('');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue;
        if (type === 'checkbox') {
            newValue = checked;
        } else if (name === 'ordinal') {
            newValue = value === '' ? '' : parseInt(value, 10);
        } else {
            newValue = value;
        }
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) { setError('Name is required'); return; }
        if (!formData.code.trim()) { setError('Code is required'); return; }

        setSaving(true);
        setError('');

        try {
            const data = {
                organization_id: organization.id,
                name: formData.name.trim(),
                code: formData.code.trim().toUpperCase(),
                ordinal: parseInt(formData.ordinal, 10) || 0,
                is_active: formData.is_active
            };

            let response;
            if (editingItem) {
                response = await apiService.updateStorageLocationType(editingItem.id, data);
            } else {
                response = await apiService.createStorageLocationType(data);
            }

            if (response.success) {
                handleCloseModal();
                await loadItems();
            } else {
                setError(response.error || 'Failed to save storage location type');
            }
        } catch (err) {
            console.error('Error saving storage location type:', err);
            setError('Failed to save storage location type');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <Button variant="outline-secondary" onClick={onBack} className="me-3">
                        <i className="bi bi-arrow-left me-1"></i> Back
                    </Button>
                    <span className="fs-4 fw-bold">
                        <i className="bi bi-geo-alt me-2"></i>Storage Location Type Management
                    </span>
                    <p className="text-muted mt-2 mb-0">Manage storage location types for {organization.name}</p>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </Alert>
            )}

            <Card className="shadow-sm">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0"><i className="bi bi-list-check me-2"></i>Storage Location Types</h5>
                        <div className="d-flex align-items-center">
                            {!loading && items.length > 0 && (
                                <Badge bg="info" className="me-3">{getFilteredAndSorted().length} / {items.length}</Badge>
                            )}
                            <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
                                <i className="bi bi-plus-circle me-1"></i> Add Storage Location Type
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" /><p className="mt-3 text-muted">Loading...</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-geo-alt display-1"></i>
                            <div className="mt-2">No storage location types found for this organization</div>
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold"><i className="bi bi-search me-1"></i>Search by Name</Form.Label>
                                        <Form.Control type="text" placeholder="Enter name..." value={searchName} onChange={(e) => setSearchName(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold"><i className="bi bi-search me-1"></i>Search by Code</Form.Label>
                                        <Form.Control type="text" placeholder="Enter code..." value={searchCode} onChange={(e) => setSearchCode(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            {(searchName || searchCode) && (
                                <div className="mb-3">
                                    <Button variant="outline-secondary" size="sm" onClick={() => { setSearchName(''); setSearchCode(''); }}>
                                        <i className="bi bi-x-circle me-1"></i>Clear Filters
                                    </Button>
                                </div>
                            )}
                            <Table striped bordered hover responsive>
                                <thead className="table-dark">
                                    <tr>
                                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name {getSortIcon('name')}</th>
                                        <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>Code {getSortIcon('code')}</th>
                                        <th onClick={() => handleSort('ordinal')} style={{ cursor: 'pointer' }}>Ordinal {getSortIcon('ordinal')}</th>
                                        <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer' }}>Active {getSortIcon('is_active')}</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getFilteredAndSorted().map(item => (
                                        <tr key={item.id}>
                                            <td>{item.name || '-'}</td>
                                            <td><code className="text-primary">{item.code || '-'}</code></td>
                                            <td>{item.ordinal ?? '-'}</td>
                                            <td><Badge bg={item.is_active ? 'success' : 'secondary'}>{item.is_active ? 'Yes' : 'No'}</Badge></td>
                                            <td>
                                                <Button variant="outline-primary" size="sm" title="Edit" onClick={() => handleShowModal(item)}>
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Storage Location Type' : 'Add Storage Location Type'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        {error && <Alert variant="danger" dismissible onClose={() => setError('')}><i className="bi bi-exclamation-triangle me-2"></i>{error}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>Organization</Form.Label>
                            <Form.Control type="text" value={organization?.name || ''} disabled readOnly />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter name" required maxLength={255} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Code *</Form.Label>
                            <Form.Control type="text" name="code" value={formData.code} onChange={handleInputChange} placeholder="Enter code" required style={{ textTransform: 'uppercase' }} disabled={editingItem !== null} />
                            <Form.Text className="text-muted">{editingItem ? 'Code cannot be changed once created.' : 'Will be converted to uppercase. Unique per organization.'}</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Ordinal</Form.Label>
                            <Form.Control type="number" name="ordinal" value={formData.ordinal} onChange={handleInputChange} placeholder="Enter ordinal" min={0} />
                            <Form.Text className="text-muted">Used to control display order.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} label="Active" />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default StorageLocationTypeManagement;
